import get from 'lodash/get';

export const numberValidation = /^\s*(?=.*[1-9])\d*(?:\.\d{1,2})?\s*$/;

export const paramsFromURL = (urlString: string | null = null): Record<string, string> => {
  const vars = urlString ? urlString.split('&') : window.location.search.split('&');
  const queryString = {};

  if (vars.join().trim()) {
    for (let i = 0; i < vars.length; i++) {
      const stringArr = vars[i].split('=');
      const pair = stringArr.map(item => item.replace('?', ''));
      const key = decodeURIComponent(pair[0]);
      const value = decodeURIComponent(pair[1]);

      // If first entry with this name
      if (typeof queryString[key] === 'undefined') {
        queryString[key] = decodeURIComponent(value);
        // If second entry with this name
      } else if (typeof queryString[key] === 'string') {
        const arr = [queryString[key], decodeURIComponent(value)];
        queryString[key] = arr;
        // If third or later entry with this name
      } else {
        queryString[key].push(decodeURIComponent(value));
      }
    }
  }

  return queryString;
};

// Auth0's OIDC redirect callback lands on this app's URL with `?code=...&state=...` appended
// alongside whatever query params launched the session (see AuthService.completeLogin, which
// preserves everything except these two) — or, on a failed login, `?error=...&error_description=
// ...&state=...` instead. These happen to be valid base64 alphabet strings, so naively decoding
// whichever query key comes last would clobber the real initData blob with garbage right as the
// callback page loads — before completeLogin() ever runs to strip them. Skip these reserved OIDC
// params so they're never mistaken for the initData key.
const OIDC_CALLBACK_PARAMS = new Set(['code', 'state', 'error', 'error_description']);

export const decodeQueryParams = (queryObject): Record<string, string> => {
  let decodedQueryObject = {};
  for (const key of Object.keys(queryObject)) {
    if (OIDC_CALLBACK_PARAMS.has(key)) {
      continue;
    }
    // Any other unexpected key (not just the ones above) can still trip this up: decodeB64
    // returns '' for a key that isn't valid base64, and paramsFromURL('') falls into its
    // "no urlString given" branch and parses window.location.search instead of nothing — and a
    // key that *does* decode can produce garbage bytes containing a bare '%' that isn't valid
    // percent-encoding, which throws inside paramsFromURL's decodeURIComponent call. Guard both:
    // skip empty decodes, and skip (rather than crash on) a decode that doesn't parse cleanly.
    const decoded = decodeB64(key);
    if (!decoded) {
      continue;
    }
    try {
      decodedQueryObject = paramsFromURL(decoded);
    } catch {
      continue;
    }
  }
  return decodedQueryObject;
};

export const monthInText = [
  { full: 'January', short: 'Jan' },
  { full: 'February', short: 'Feb' },
  { full: 'March', short: 'Mar' },
  { full: 'April', short: 'Apr' },
  { full: 'May', short: 'May' },
  { full: 'June', short: 'Jun' },
  { full: 'July', short: 'Jul' },
  { full: 'August', short: 'Aug' },
  { full: 'September', short: 'Sep' },
  { full: 'October', short: 'Oct' },
  { full: 'November', short: 'Nov' },
  { full: 'December', short: 'Dec' },
];

export interface OptionItem {
  label: string;
  value: string | number;
}

export const debitDateOptions = (days: number = 7): Array<OptionItem> => {
  let dateOptions: Array<OptionItem> = [];
  const today = new Date();

  for (let index = 0; index < days; index++) {
    const current = dateOptions.length;
    if (current === 0) {
      dateOptions[current] = {
        label: 'Today',
        value: 'today',
      };
    } else if (current === 1) {
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      dateOptions[current] = {
        label: 'Tomorrow',
        value: tomorrow.getTime(),
      };
    } else {
      const date = new Date();
      date.setDate(today.getDate() + current);
      dateOptions[current] = {
        label: `In ${current} days (${date.getDate()}-${monthInText[date.getMonth()].short})`,
        value: date.getTime(),
      };
    }
  }
  return dateOptions;
};

export const searchPayMethodByToken = (
  payMethods: Record<string, string>[] | [] | null,
  tokenP: string,
): Record<string, string> | false => {
  if (payMethods && payMethods.length) {
    let pMethod: false | Record<string, string> = false;
    payMethods.forEach(element => {
      const { token } = element;
      if (token === tokenP) {
        pMethod = element;
      }
    });

    return pMethod;
  }

  return false;
};

// Repeated "add new payment method" attempts with the same card/bank details create a fresh
// finance-core row each time for debit accounts (finance-core only dedupes cards, matching on
// token — see createPaymentMethod in payments.service.ts). Card entries key on token, the same
// identifier finance-core itself dedupes on; debit entries have no token, so key on bsb+last4 —
// the same masked fields already shown in the UI (PaymentMethod.tsx), so no need to fetch the
// full account number just to dedupe.
const paymentMethodDedupeKey = (method: any): string => {
  if (method.token) {
    return `card:${method.token}`;
  }
  // bsb/last4 can be absent/redacted — falling back to bsb+last4 alone would collapse distinct
  // debit methods into the same "debit:undefined:undefined" key, so key on id instead when
  // either field is missing.
  if (!method.accountBsb || !method.accountLast4) {
    return `debit:${method.id}`;
  }
  return `debit:${method.accountBsb}:${method.accountLast4}`;
};

export const dedupePaymentMethods = (paymentMethods: any[]): any[] => {
  const seen = new Set<string>();
  return paymentMethods.filter(method => {
    const key = paymentMethodDedupeKey(method);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const decodeB64 = (code: string): string => {
  try {
    return window.atob(code);
  } catch (error) {
    return '';
  }
};

export const encodeB64 = (val: string): string => {
  try {
    return window.btoa(val);
  } catch (error) {
    return '';
  }
};

export const cleanSessionLater = (milliseconds: number = 2000): void => {
  setTimeout(() => window.sessionStorage.clear(), milliseconds); // TODO find a better way to clean store
};

export const cleanStoreLater = (milliseconds: number = 100): void => {
  setTimeout(() => {
    window.sessionStorage.clear();
    window.localStorage.clear();
  }, milliseconds);
};

export const isValidJson = (str: string) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const getUserId = (initData: Record<string, any>): number => {
  const { portalUserId, isAdmin } = initData;

  if (isAdmin === '1') {
    // Falls back to 0 (rather than NaN) consistently with the non-admin branch below — a
    // missing/non-numeric portalUserId would otherwise produce a "UNaN" publicId that fails
    // lookups in a hard-to-diagnose way.
    return parseInt(portalUserId) || 0;
  }
  const userIdFromInitData = get(initData, 'userId', '');
  const userId = parseInt(userIdFromInitData.replace('U', ''));
  return userId || 0;
};

// finance-core's GraphQL resolvers look up users by publicId, always formatted "U<id>" (see
// findUniqueUserByUserID in payments.service.ts) — use this wherever a userId is sent to the
// gateway, instead of getUserId()'s bare number, to keep the "U" prefix in one place rather than
// reconstructing it ad hoc at every call site (a forgotten prefix fails the lookup silently).
export const getPublicUserId = (initData: Record<string, any>): string => `U${getUserId(initData)}`;
